import { Button,Input } from 'components/atoms/index';
import TaskCard from '@/components/organisms/Card/Exam/TaskCard';
import Toast from '@/components/atoms/Toast/Toast';
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import { academyPaperApi, studentExamApi } from '@/services/api';

// 로그인 페이지
export default function StudentExamPaper() {
    const navigate = useNavigate();
    const location = useLocation();
    const slugPrefix = getSlugPrefix(location.pathname);
    const [isVisible, setIsVisible] = useState(false);
    const [toastStatus, setToastStatus] = useState<'success' | 'error' | 'info'>('success')
    const [toastMessage, setToastMessage] = useState('')
    const [isStickyVisible, setIsStickyVisible] = useState(true);
    const headerRef = useRef<HTMLDivElement>(null);

    const [paperTitle, setPaperTitle] = useState('');
    const [aiScoring, setAiScoring] = useState<boolean>(false);
    const [timeLimit, setTimeLimit] = useState<number | null>(null);
    const [items, setItems] = useState<any[]>([]);
    const [examResultId, setExamResultId] = useState<number | null>(null);
    const [selectedMap, setSelectedMap] = useState<Record<number, number[]>>({});
    const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
    const [timerActive, setTimerActive] = useState(false);
    const [warningCount, setWarningCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [paperId, setPaperId] = useState<number | null>(null);
    const pageSize = 6;

    const handleShowToast = (status: 'success' | 'error' | 'info', message: string) => {
        setToastStatus(status);
        setToastMessage(message);
        setIsVisible(false);
        setTimeout(() => {
            setIsVisible(true);
        }, 10);
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsStickyVisible(entry.isIntersecting);
            },
            { threshold: 0 }
        );

        if (headerRef.current) {
            observer.observe(headerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get('id');
        if (!id) {
            navigate(`${slugPrefix}/exam/list`);
            return;
        }
        const pid = parseInt(id, 10);
        setPaperId(pid);
        
        // 시험지 정보 먼저 로드 (timeLimit 필요)
        const initExam = async () => {
            const limit = await loadPaperDetail(pid);
            await startExam(pid, limit);
        };
        initExam();
        
        // 로컬스토리지에서 기존 진행 데이터 복구
        const storageKey = `exam_${pid}_answers`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setSelectedMap(parsed);
            } catch {}
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const startExam = async (paperId: number, limitOverride?: number | null) => {
        try {
            const res: any = await studentExamApi.start(paperId);
            const data = res?.data || res;
            const rid = Number(data?.examResultId || data?.id);
            const startedAt = data?.startedAt; // ISO 문자열 (예: "2025-11-05T14:34:19")
            
            if (rid) {
                setExamResultId(rid);
                
                // 시작 시간 기반 남은 시간 계산
                const effectiveLimit = typeof limitOverride === 'number' ? limitOverride : timeLimit;
                if (startedAt && effectiveLimit && effectiveLimit > 0) {
                    const started = new Date(startedAt).getTime();
                    const now = Date.now();
                    const elapsed = Math.floor((now - started) / 1000); // 경과 시간(초)
                    const totalSeconds = effectiveLimit * 60;
                    const remaining = Math.max(0, totalSeconds - elapsed);
                    setRemainingSeconds(remaining);
                    setTimerActive(remaining > 0);
                }
            }
        } catch (e) {
            handleShowToast('error', '시험 시작에 실패했습니다. 응시 권한을 확인해주세요.');
        }
    };

    const mapQuestionType = (qt: string): 'multiple' | 'short' | 'essay' => {
        switch ((qt || '').toUpperCase()) {
            case 'MULTIPLE_CHOICE':
                return 'multiple';
            case 'SHORT_ANSWER':
                return 'short';
            case 'ESSAY':
                return 'essay';
            default:
                return 'short';
        }
    };

    const parseChoices = (choices: any): string[] => {
        if (!choices) return [];
        if (Array.isArray(choices)) return choices as string[];
        try {
            const parsed = JSON.parse(choices);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    const isMultiAnswer = (answer: any): boolean => {
        if (!answer) return false;
        if (Array.isArray(answer)) return true;
        try {
            const parsed = typeof answer === 'string' ? JSON.parse(answer) : answer;
            return Array.isArray(parsed);
        } catch {
            return false;
        }
    };

    const loadPaperDetail = async (paperId: number): Promise<number | null> => {
        try {
            const res: any = await academyPaperApi.detail(paperId);
            const data = res?.data || res;
            setPaperTitle(data?.title || '');
            setAiScoring(!!data?.aiScoring);
            const tl = typeof data?.timeLimit === 'number' ? data.timeLimit : null;
            setTimeLimit(tl);
            const rawItems = Array.isArray(data?.items) ? data.items : [];
            setItems(rawItems);
            // 타이머는 startExam에서 시작 시간 기준으로 설정됨
            return tl;
        } catch (e) {
            handleShowToast('error', '시험 정보를 불러올 수 없습니다.');
            return null;
        }
    };

    const getByteLength = (value: string) => {
        try {
            return new TextEncoder().encode(value).length;
        } catch {
            return value.length;
        }
    };

    const validateAnswers = () => {
        for (let idx = 0; idx < items.length; idx++) {
            const it = items[idx];
            const itemId: number = (typeof it?.id === 'number' ? it.id : idx) as number;
            const qType = mapQuestionType(it?.questionType);
            if (qType === 'multiple') {
                const sel = selectedMap[itemId] || [];
                if (sel.length === 0) {
                    return {
                        ok: false,
                        message: `${idx + 1}번 객관식 문항의 답을 선택해주세요.`
                    };
                }
                continue;
            }

            const textKey = `text_${itemId}`;
            const rawValue: string = (selectedMap as any)[textKey] || '';
            const trimmed = rawValue.trim();
            if (!trimmed.length) {
                return {
                    ok: false,
                    message: `${idx + 1}번 문항의 답변을 입력해주세요.`
                };
            }

            if (qType === 'short') {
                const byteLen = getByteLength(trimmed);
                if (byteLen < 1) {
                    return {
                        ok: false,
                        message: `${idx + 1}번 주관식 답변은 1글자 이상 입력해주세요.`
                    };
                }
            }
        }
        return { ok: true };
    };

    // 타이머 카운트다운
    useEffect(() => {
        if (!timerActive || remainingSeconds == null) return;
        if (remainingSeconds <= 0) {
            setTimerActive(false);
            handleShowToast('info', '시험 시간이 종료되었습니다.');
            if (examResultId) {
                studentExamApi.submit(examResultId, { cause: 'time_over' })
                .then(()=>{
                    setTimeout(()=> navigate(`${slugPrefix}/exam/list`), 1000);
                })
                .catch(()=>{});
            }
            return;
        }
        const id = setTimeout(() => setRemainingSeconds(remainingSeconds - 1), 1000);
        return () => clearTimeout(id);
    }, [timerActive, remainingSeconds]);

    const formatTime = (sec: number | null) => {
        if (sec == null) return '—';
        const s = Math.max(0, sec);
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const rem = s % 60;
        const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
        const ss = String(rem).padStart(2, '0');
        return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
    };

    // 응시 화면 이탈 경고 로직
    useEffect(() => {
        const lastWarnAtRef = { current: 0 } as { current: number };
        const warnOnce = () => {
            const now = Date.now();
            if (now - lastWarnAtRef.current < 800) return; // 중복 이벤트 디바운스
            lastWarnAtRef.current = now;
            setWarningCount(prev => {
                const next = prev + 1;
                handleShowToast('error', `응시화면 이탈 경고 ${next}/3`);
                if (next >= 3 && examResultId) {
                    studentExamApi.submit(examResultId, { cause: 'warning_auto' })
                    .then(()=>{
                        setTimeout(()=> navigate(`${slugPrefix}/exam/list`), 800);
                    })
                    .catch(()=>{});
                }
                return next;
            });
        };

        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') {
                warnOnce();
            }
        };
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            warnOnce();
            e.preventDefault();
            e.returnValue = '';
        };
        const handleBlur = () => {
            // 창 포커스 잃음(다른 창 올라옴/최소화 등)
            warnOnce();
        };
        const handlePageHide = () => {
            // 모바일/사파리 등 백그라운드 전환
            warnOnce();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('blur', handleBlur);
        window.addEventListener('pagehide', handlePageHide);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [examResultId]);

    // 3분 간격 로컬 자동 저장
    useEffect(() => {
        if (!paperId) return;
        const id = setInterval(() => {
            const storageKey = `exam_${paperId}_answers`;
            localStorage.setItem(storageKey, JSON.stringify(selectedMap));
            handleShowToast('info', '응시 내역이 자동 저장되었습니다.');
        }, 180000); // 3분
        return () => clearInterval(id);
    }, [paperId, selectedMap]);

  return (
        <main 
            className='flex flex-1 justify-center w-full bg-white lg:my-10 card md:bg-transparent'
            onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                    e.preventDefault();
                }
            }}
        >
        <div className='flex flex-col pt-8 w-full lg:pt-0'>
            <div ref={headerRef} className='flex justify-between items-center px-5 py-3 w-full back_gra_ravendar lg:!bg-none'>
                <h2 className='w-full text-lg font-bold lg:text-2xl'>
                    {paperTitle}
                    {paperTitle && (
                        <span className='ml-2 text-sm font-medium text-gray-500'>
                            {aiScoring ? 'AI 채점' : '일반 채점'}
                        </span>
                    )}
                </h2>
                <p className={`px-3 py-1 font-bold text-blue-500 bg-white text-lg rounded-2xl ${
                        isStickyVisible 
                            ? 'sticky top-4 z-10' 
                            : 'fixed top-4 right-8 z-50 shadow-sm'
                    }`}>
                    {formatTime(remainingSeconds)}
                </p>
            </div>
            <div className='px-5 py-2 bg-gray-50'>
                <p className='text-xs text-red-400'>응시 도중, 브라우저 탭을 내리면 경고 처리되며,
                경고가 3회 누적 될 경우 즉시 시험이 제출됩니다. , 3분마다 풀이중인 문항에 대한 내용은 자동저장됩니다.</p>
            </div>
            {/* 문제 카드 section - 페이징 (6문항/페이지, 좌 3개/우 3개) */}
            {(() => {
                const start = (currentPage - 1) * pageSize;
                const end = start + pageSize;
                const pageItems = items.slice(start, end);
                const leftItems = pageItems.slice(0, Math.min(3, pageItems.length));
                const rightItems = pageItems.slice(Math.min(3, pageItems.length));
                const renderCard = (item: any, absoluteIdx: number) => {
                    const qType = mapQuestionType(item?.questionType);
                    const choices = parseChoices(item?.choices);
                    const options = choices.map((c: string, i: number) => ({ id: i + 1, word: String(c) }));
                    const hasImage = !!item?.imageUrl;
                    const hasListen = !!item?.audioUrl;
                    const questionText = `${absoluteIdx + 1}. ${item?.questionText || ''}`;
                    const itemId: number = (typeof item?.id === 'number' ? item.id : absoluteIdx) as number;
                    const allowMulti = isMultiAnswer(item?.answer);
                    const selected = selectedMap[itemId] || [];
                    const onToggle = (optId: number) => {
                        setSelectedMap(prev => {
                            const cur = prev[itemId] || [];
                            let next: number[];
                            if (allowMulti) {
                                next = cur.includes(optId) ? cur.filter(x => x !== optId) : [...cur, optId];
                            } else {
                                next = cur.length === 1 && cur[0] === optId ? [] : [optId];
                            }
                            // 로컬에만 저장, 서버는 제출 시에만
                            return { ...prev, [itemId]: next };
                        });
                    };
                    const textKey = `text_${itemId}`;
                    const textValue = (selectedMap as any)[textKey] || '';
                    const onChangeText = (val: string) => {
                        // 로컬에만 저장, 서버는 제출 시에만
                        setSelectedMap(prev => ({ ...prev, [textKey]: val }));
                    };
                    return (
                        <TaskCard
                            key={item?.id || absoluteIdx}
                            className=''
                            questionType={qType}
                            question={questionText}
                            options={options}
                            hasImage={hasImage}
                            hasListenButton={hasListen}
                            imageUrl={item?.imageUrl || null}
                            audioUrl={item?.audioUrl || null}
                            selectedOptionIds={selected}
                            onToggleOption={onToggle}
                            valueText={textValue}
                            onChangeText={onChangeText}
                            isExamMode={true}
                        />
                    );
                };
                return (
                    <section className='lg:grid lg:grid-cols-2 lg:gap-0 *:py-5 lg:*:pb-10 *:px-5'>
                        <div className='flex flex-col gap-5'>
                            {leftItems.map((it, i) => renderCard(it, start + i))}
                        </div>
                        <div className='flex flex-col gap-5'>
                            {rightItems.map((it, i) => renderCard(it, start + 3 + i))}
                        </div>
                    </section>
                );
            })()}
            {/* 페이지 네비게이션 */}
            <div className='flex flex-wrap gap-2 justify-center p-3'>
                {Array.from({ length: Math.max(1, Math.ceil(items.length / pageSize)) }).map((_, i) => (
                    <button
                        key={i}
                        className={`px-3 py-1 rounded-full border ${currentPage === i+1 ? 'border-indigo-500 text-indigo-600' : 'border-gray-300 text-gray-600'}`}
                        onClick={() => {
                            // 페이지 전환 시 로컬스토리지에 저장
                            if (paperId) {
                                localStorage.setItem(`exam_${paperId}_answers`, JSON.stringify(selectedMap));
                            }
                            setCurrentPage(i+1);
                        }}
                    >{i+1}</button>
                ))}
            </div>
            {/* 제출 버튼만 남김 */}
            <div className='flex gap-2 justify-center p-5'>
                <Button size="large" intent='primary' className='w-full lg:max-w-80'
                    disabled={submitting}
                    onClick={async ()=>{
                        // 제출 검증: 부족한 문항/바이트 안내
                        const validation = validateAnswers();
                        if (!validation.ok) {
                            handleShowToast('error', validation.message || '모든 문항에 답변해야 제출 가능합니다.');
                            return;
                        }
                        if (examResultId) {
                            try {
                                setSubmitting(true);
                                console.log('[EXAM_SUBMIT] Starting submission, examResultId:', examResultId);
                                console.log('[EXAM_SUBMIT] Total items:', items.length);
                                console.log('[EXAM_SUBMIT] selectedMap:', selectedMap);
                                
                                // 제출 시점에 모든 답안을 서버로 전송
                                let savedCount = 0;
                                for (let idx = 0; idx < items.length; idx++) {
                                    const it = items[idx];
                                    const itemId: number = (typeof it?.id === 'number' ? it.id : idx) as number;
                                    const qType = mapQuestionType(it?.questionType);
                                    
                                    if (qType === 'multiple') {
                                        const sel = selectedMap[itemId] || [];
                                        if (sel.length > 0) {
                                            console.log(`[EXAM_SUBMIT] Saving multiple choice itemId=${itemId}, selected=`, sel);
                                            await studentExamApi.saveAnswer(examResultId, {
                                                itemId,
                                                answerType: 'multiple',
                                                selectedOptionIds: sel,
                                            });
                                            savedCount++;
                                        } else {
                                            console.log(`[EXAM_SUBMIT] Skip empty multiple choice itemId=${itemId}`);
                                        }
                                    } else {
                                        const textKey = `text_${itemId}`;
                                        const val: string = (selectedMap as any)[textKey] || '';
                                        if (val.trim().length > 0) {
                                            console.log(`[EXAM_SUBMIT] Saving text answer itemId=${itemId}, answerType=${qType}, length=${val.length}`);
                                            await studentExamApi.saveAnswer(examResultId, {
                                                itemId,
                                                answerType: qType === 'essay' ? 'essay' : 'short',
                                                answerText: val,
                                            });
                                            savedCount++;
                                        } else {
                                            console.log(`[EXAM_SUBMIT] Skip empty text answer itemId=${itemId}`);
                                        }
                                    }
                                }
                                
                                console.log(`[EXAM_SUBMIT] Saved ${savedCount}/${items.length} answers`);
                                
                                // 제출 완료
                                console.log('[EXAM_SUBMIT] Calling submit API');
                                await studentExamApi.submit(examResultId, { cause: 'normal' });
                                
                                // 로컬스토리지 정리
                                if (paperId) {
                                    localStorage.removeItem(`exam_${paperId}_answers`);
                                }
                                
                                handleShowToast('success', '제출이 완료되었습니다.');
                                setTimeout(()=> navigate(`${slugPrefix}/exam/list`), 900);
                            } catch (e) {
                                console.error('[EXAM_SUBMIT] Error:', e);
                                handleShowToast('error', '제출에 실패했습니다. 잠시 후 다시 시도해주세요.');
                            } finally {
                                setSubmitting(false);
                            }
                        }
                    }}
                >{submitting ? '제출 중...' : '제출하기'}</Button>
            </div>
        <Toast 
            position='top-center'    
            status={toastStatus}
            message={toastMessage}
            isVisible={isVisible}
        />
        
        </div>
    </main>
  )
}